import { ExtranetOrderTicketDetailsDto } from '@pharmac-extranet/extranet-api'
import {
  ExtranetContainer,
  ExtranetTableColumn,
  ExtranetTableRow,
  formatDateString,
  formatDateTimeString,
  useBreakpoint,
  useLanguage,
} from '@pharmac-extranet/shared'
import {
  PharmacCheckbox,
  PharmacIcon,
  PharmacSuccessToast,
  PharmacText,
  PharmacTooltip,
  usePharmacToast,
} from '@pharmac/ui'
import { UseQueryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCustomerContext } from '../../features/customer/CustomerProvider'
import { api } from '../../services/HttpService'
import { CatalogItemAvailability } from '../dose-dispensing-catalog/CatalogItemAvailability'
import { HoverColumn } from '../product-shortages/ProductShortagesCustomerRow'
import { getOrderTicketsQueryKeyBase } from './util/order-ticket-query-key-factory'

type Props = {
  ticket: any
  requestChecked?: boolean
}

export const OrderTicketOutOfStockProductsMobileView = ({ requestChecked, ticket }: Props) => {
  const { t, key } = useLanguage()
  const { maxMobile } = useBreakpoint()
  const [checked, updateChecked] = useState<boolean>(false)
  const { customer } = useCustomerContext()
  const queryClient = useQueryClient()
  const toastHook = usePharmacToast()
  const navigate = useNavigate()

  const getOrderTicketById = (
    ticketId?: number,
    customerId?: number
  ): UseQueryOptions<ExtranetOrderTicketDetailsDto> => ({
    queryFn: async () =>
      await api.orders.getOrderTicketById(ticketId as number, customerId as number),
    queryKey: ['__orderTicketById', customerId, ticketId],
    enabled: typeof customerId === 'number' && typeof ticketId === 'number',
  })
  const { data: ticketById } = useQuery(getOrderTicketById(ticket.id, customer?.id))

  const { mutate: confirmTicket } = useMutation({
    mutationFn: () => api.orders.confirmOrderTicket(Number(customer?.id), Number(ticketById?.id)),
    onSuccess: async () => {
      toastHook.add({
        autoClose: true,
        autoCloseTimeout: 4000,
        toast: (
          <PharmacSuccessToast>
            {t('extranet:correctionRequests.requesCheckedSuccess')}
          </PharmacSuccessToast>
        ),
      })
      await queryClient.invalidateQueries({
        queryKey: getOrderTicketsQueryKeyBase(customer?.id),
      })
      navigate(0)
    },
  })

  return (
    <ExtranetContainer
      full
      flexColumn
    >
      <ExtranetContainer
        flexColumn
        full
        paddingMediumHorizontal
        paddingMediumVertical
        style={{ borderTop: '1px solid var(--color-accent-4)' }}
      >
        <PharmacText paddingSmallBottom>{t('extranet:orderHistory.dateOfBirth')}</PharmacText>
        <PharmacText
          weight="bold"
          paddingMediumBottom
        >
          {formatDateString(ticket.birthDate)}
        </PharmacText>
        <PharmacText paddingSmallBottom>{t('extranet:orderHistory.orderNumber')}</PharmacText>
        <PharmacText
          weight="bold"
          paddingMediumBottom
        >
          {ticket.orderId}
        </PharmacText>
        <PharmacText paddingSmallBottom>
          {t('extranet:orderHistory.subscriptionPeriod')}
        </PharmacText>
        <PharmacText
          weight="bold"
          paddingMediumBottom
        >
          {formatDateString(ticket.startDate)} - {formatDateString(ticket.endDate)}
        </PharmacText>

        <PharmacText paddingMediumBottom>{t('medicines.availableForCustomers')}</PharmacText>
        {ticketById?.outOfStockProducts?.map((product, i) => (
          <ExtranetTableRow
            key={i}
            expand={
              <ExtranetContainer
                flexColumn
                full
                paddingMediumHorizontal
                paddingMediumVertical
                style={{
                  backgroundColor: 'var(--color-accent-1)',
                  borderTop: '4px solid var(--color-accent-3)',
                }}
              >
                <ExtranetContainer
                  flexRow
                  alignCenter
                >
                  <div style={{ fontWeight: 'bold' }}>
                    <CatalogItemAvailability availability={product.outOfStockProductAvailability} />
                  </div>
                </ExtranetContainer>
                {product.substituteProductNumber !== null && (
                  <>
                    <PharmacText
                      paddingMediumBottom
                      paddingLargeTop
                    >
                      {t('extranet:correctionRequests.substituteProduct')}:
                    </PharmacText>
                    <ExtranetContainer full>
                      <ExtranetTableRow
                        key={product.substituteProductNumber}
                        children={
                          <>
                            <ExtranetTableColumn
                              size={maxMobile ? 0.7 : 0.3}
                              style={{
                                backgroundColor: 'var(--extranet-light-purple)',
                                justifyContent: 'center',
                              }}
                            >
                              <ExtranetContainer
                                flexRow
                                alignCenter
                              >
                                <PharmacIcon icon="refreshCcw" />
                              </ExtranetContainer>
                            </ExtranetTableColumn>
                            <HoverColumn
                              style={{ backgroundColor: 'var(--color-accent-1)' }}
                              size={maxMobile ? 3 : 4}
                            >
                              <ExtranetContainer flexRow>
                                <PharmacText
                                  paddingMediumHorizontal={!maxMobile}
                                  paddingSmallHorizontal={maxMobile}
                                  style={{ textDecoration: 'underline' }}
                                >
                                  {product.substituteProductNumber}{' '}
                                  {key(product.substituteProductName)}{' '}
                                  {product.substituteProductStrength}
                                </PharmacText>
                              </ExtranetContainer>
                            </HoverColumn>
                          </>
                        }
                      />
                    </ExtranetContainer>
                  </>
                )}
              </ExtranetContainer>
            }
            isExpanded
            toggleExpand={() => null}
            children={
              <>
                <ExtranetTableColumn
                  size={maxMobile ? 1 : 0.5}
                  style={{
                    backgroundColor: 'var(--extranet-light-purple)',
                    justifyContent: 'center',
                  }}
                >
                  <PharmacText weight="bold">{product.outOfStockProductNumber}</PharmacText>
                </ExtranetTableColumn>
                <HoverColumn
                  style={{ backgroundColor: 'var(--color-accent-1)' }}
                  marginMediumLeft={!maxMobile}
                  size={maxMobile ? 3 : 4}
                >
                  <PharmacText weight="bold">
                    {key(product.outOfStockProductName)} {product.strength}
                  </PharmacText>
                </HoverColumn>
              </>
            }
          />
        ))}
      </ExtranetContainer>

      <ExtranetContainer
        full
        flexColumn
        paddingMediumHorizontal
      >
        <PharmacText
          weight="semibold"
          paddingSmallBottom
        >
          {t('common:comment')}
        </PharmacText>
        {ticketById?.outOfStockProducts.map((product, i) => (
          <ExtranetContainer key={i}>
            {product?.comment && (
              <>
                <PharmacText>{product.comment}</PharmacText>
              </>
            )}
          </ExtranetContainer>
        ))}
      </ExtranetContainer>
      <ExtranetContainer
        full
        flexColumn
        marginMediumTop
        paddingMediumTop
        style={{ backgroundColor: requestChecked ? 'var(--color-accent-2)' : undefined }}
      >
        {requestChecked && (
          <ExtranetContainer
            full
            paddingMediumHorizontal
            flexColumn={maxMobile}
            flexRow={!maxMobile}
          >
            <PharmacText
              paddingSmallBottom
              paddingSmallRight
            >
              {t('extranet:correctionRequests.requesCheckedBy')}:
            </PharmacText>
            <PharmacText
              marginSmallRight
              weight="semibold"
            >
              {ticketById?.isExpired && (
                <>
                  {ticketById?.expiredAt &&
                    formatDateTimeString(new Date(ticketById?.expiredAt).toISOString())}{' '}
                  {'Pharmac Finland Oy'}
                </>
              )}
              {ticketById?.customerSignature && (
                <>
                  {ticketById?.confirmedByCustomerAt &&
                    formatDateTimeString(ticketById?.confirmedByCustomerAt)}{' '}
                  {ticketById?.customerSignature}
                </>
              )}
            </PharmacText>
          </ExtranetContainer>
        )}
        <ExtranetContainer
          full
          flexRow
          alignCenter
          justifyEnd
          paddingLargeTop
        >
          <PharmacTooltip
            marginMediumRight
            maxWidth={maxMobile ? '295px' : '500px'}
            handle={
              <PharmacIcon
                style={{ color: 'var(--color-info)' }}
                size="25px"
                icon="info"
              />
            }
          >
            <PharmacText>{t('extranet:correctionRequests.requestCheckTooltip')}</PharmacText>
          </PharmacTooltip>
          <ExtranetContainer
            fourth
            paddingSmallVertical
            paddingMediumHorizontal
            alignCenter
            style={{
              cursor: requestChecked ? 'not-allowed' : undefined,
              backgroundColor: requestChecked ? 'var(--color-accent-4)' : ' var(--color-yellow)',
              borderRadius: '15px 0 0',
              width: 'fit-content',
            }}
          >
            <PharmacCheckbox
              style={{
                cursor: requestChecked ? 'not-allowed' : undefined,
                opacity: requestChecked ? '0.8' : undefined,
              }}
              checked={requestChecked ? true : checked}
              disabled={requestChecked ? true : false}
              handleChange={(event) => {
                updateChecked(event.target.checked), confirmTicket()
              }}
            >
              <PharmacText
                weight="bold"
                marginSmallLeft
                style={{
                  opacity: requestChecked ? '0.6' : undefined,
                }}
              >
                {ticketById?.isExpired ? (
                  <>{t('extranet:correctionRequests.requestCheckByPharmac')}</>
                ) : (
                  <>{t('extranet:correctionRequests.requestCheck')}</>
                )}
              </PharmacText>
            </PharmacCheckbox>
          </ExtranetContainer>
        </ExtranetContainer>
      </ExtranetContainer>
    </ExtranetContainer>
  )
}
